<script>
  import { getContext } from "svelte";
  import uniqBy from "lodash/uniqBy";
  import cloneDeep from "lodash/cloneDeep";
  import { Modal, tooltip } from "svelte-utilities";

  import SidebarSection from "./SidebarSection.svelte";
  import SidebarTitle from "./SidebarTitle.svelte";
  import DimensionProperties from "./DimensionProperties.svelte";
  import ItemProperties from "./ItemProperties.svelte";
  import CollectionItemList from "./CollectionItemList.svelte";
  import TypeProperties from "./TypeProperties.svelte";
  import DocumentNoteProperties from "./DocumentNoteProperties.svelte";
  import LayerProperties from "./LayerProperties.svelte";
  import LocationThumbnailViewer from "#lib/sidebar/LocationThumbnailViewer.svelte";
  import Attachments from "./Attachments.svelte";
  import TextInput from "./TextInput.svelte";
  import SelectInput from "./SelectInput.svelte";
  import ButtonInput from "./ButtonInput.svelte";
  import ColorInput from "#lib/sidebar/ColorInput.svelte";
  import TextareaInput from "#lib/sidebar/TextareaInput.svelte";
  import CustomScaleModal from "#lib/sidebar/CustomScaleModal.svelte";
  import { withClipScale } from "#lib/framex/clip-scale.js";

  import SpinnerIcon from "@local/assets/icons/spinner.svg";
  import EllipsisIcon from "@local/assets/icons/ellipsis.svg";
  import LocationItemIcon from "@local/assets/icons/location-item.svg";
  import LocationCollIcon from "@local/assets/icons/location-opening.svg";
  import LocationItemCloneMultiple from "@local/assets/icons/location-item-clone-multiple.svg";
  import ImageIcon from "@local/assets/icons/image.svg";
  import BlankIcon from "@local/assets/icons/blank.svg";
  import BooleanInput from "./BooleanInput.svelte";
  import CircleIcon from "@local/assets/icons/circle.svg";
  import SquareIcon from "@local/assets/icons/square.svg";
  import HexagonIcon from "@local/assets/icons/hexagon.svg";
  import NoteIcon from "@local/assets/icons/note.svg";
  import TriangleIcon from "@local/assets/icons/triangle.svg";
  import DiamondIcon from "@local/assets/icons/diamond.svg";
  import AnchorIcon from "@local/assets/icons/anchor.svg";
  import HardSpliceIcon from "@local/assets/icons/hard-splice.svg";
  import WorkingSpliceIcon from "@local/assets/icons/working-splice.svg";
  import ClipIcon from "@local/assets/icons/clip.svg";
  import AiIcon from "@local/assets/icons/ai.svg";

  import History from "./History.svelte";
  import SelectedLocations from "./SelectedLocations.svelte";

  import { bucketArray, bucketArrayMultiple } from "overline";
  import makeMultiItem from "#src/extensions/multi-item.js";
  import makeMultiType from "#src/extensions/multi-type.js";
  import makeMultiCategory from "#src/extensions/multi-category.js";
  import makeMultiNote from "#src/extensions/multi-category.js";
  import { locRecord } from "#src/extensions/filter-items.js";
  import { locationDrawingUpdate } from "#src/extensions/docs/location-drawing.js";
  import { alerts } from "#src/stores/alerts.js";
  import { promptLocationsParallel } from "#src/api/ai.js";
  import analyzeOpeningClips from "#src/extensions/analyze-opening-clips.js";
  import locationImage from "#src/extensions/location-image.js";
  import { scaleOptions, definedMultipliers } from "@local/extensions/utilities/scales.js";
  import PostStream from "#src/extensions/post-stream.js";
  import { profile } from "#src/stores/auth.js";

  import { incrementMark } from "@local/extensions/identifiers/mark.js";

  import {
    expandRightPanel,
    singleOpeningLlmModel,
    singleOpeningReasoningLevel,
    notePrompt,
    noteLlmModel,
    noteReasoningLevel,
  } from "#src/stores/ui.js";

  export let locations;
  export let dimensions;
  export let doc;
  export let docLocations;
  export let locationsByRecord;
  export let group;
  export let items;
  export let types;
  export let collections;
  export let disabled;
  export let customColumns;
  export let customColColumns;
  export let standardColumns;
  export let collectionItems;
  export let documentNotes;
  export let spinners;
  export let visibleIndices;

  const recordTypes = {
    item: "Item",
    collection: "Opening",
    type: "Type",
    document_note: "Document Note",
  };

  const org = getContext("org");
  const fileData = getContext("fileData");

  let refreshKey = crypto.randomUUID();
  let changeAssocModal;
  let associationType = null;
  let newAssociation = null;
  let showAdvancedLocationSettings = false;
  let requestingAiImport = false;
  let confirmPromptModal;
  let customScaleModal;

  $: loc = locations.length === 1 ? locations[0] : makeMultiCategory(locations);
  $: locTypeCounts = locations.reduce((acc, loc) => {
    acc[loc.record_type] = (acc[loc.record_type] || 0) + 1;
    return acc;
  }, {});
  $: hasCollection = locations.some((loc) => loc.record_type === "collection");
  $: allLocsNotes = locations.every((l) => l.record_type === "document_note" && l.clip);
  $: allLocsClipped = locations.every((l) => l.clip);
  $: allLocsImaged = allLocsClipped && locations.every((l) => clipIsDrawing(l));
  $: allAttachments = bucketArrayMultiple($group.attachments, "item_id", "type_id");
  $: uniqLocations = uniqBy(locations, "record_id").filter((l) => l && !!locRecord(l, $group));
  $: location = uniqLocations[0];
  $: rec = record(location, allAttachments);
  $: customCols = rec?.is_collection ? customColColumns : customColumns;
  $: multiRecord = makeMultiRecord(uniqLocations, $group, customCols);
  $: (rec || multiRecord) && refresh();
  $: locsByRecordId = bucketArray(locations, "record_id");
  $: locationCounts = countLocations(locations);
  $: itemsWithLocs = items.filter((i) => locsByRecordId[i.id]);
  $: collectionsWithLocs = collections.filter((c) => locsByRecordId[c.id]);
  $: documentNotesWithLocs = documentNotes.filter((n) => locsByRecordId[n.id]);
  $: typesWithLocs = types.filter((t) => locsByRecordId[t.id]);
  $: title = getTitle(uniqLocations, locations, location, $group);
  $: settings = $group.data.settings;
  $: typeSettings = {
    display_unit: $group.data.settings.display_unit,
    mm_precision: $group.data.settings.mm_precision,
    decimal_precision: 3,
    fractional_precision: 5,
    dimension_format: $group.data.settings.dimension_format,
  };
  $: associationOptions = getAssociationOptions(associationType);

  function getAssociationOptions(associationType) {
    if (!associationType) return [];
    if (associationType === "item") {
      return items.map((i) => ({ label: i.mark, value: i.id }));
    } else if (associationType === "collection") {
      return collections.map((c) => ({ label: c.mark, value: c.id }));
    } else if (associationType === "type") {
      return types.map((t) => ({ label: t.mark, value: t.id }));
    } else if (associationType === "document_note") {
      return documentNotes.map((n) => ({ label: n.mark, value: n.id }));
    }
  }

  function clipIsDrawing(loc) {
    const record = locRecord(loc, $group);
    return loc?.id && record?.drawing?.attachment_id === loc.id;
  }

  function createClips() {
    const ids = Array.isArray(loc.id) ? loc.id : [loc.id];
    const unclipped = ids.filter((id) => !$group.locations[id]?.clip);
    const updates = unclipped.map((id) => {
      const loc = $group.locations[id];
      const page = $group.documents[loc.document_id].data.pages[loc.page_id];
      const clip = {
        x: loc.position.x - 150,
        y: loc.position.y - 150,
        width: 300,
        height: 300,
        drawing_scale: page.scale || null,
        drawing_offset: { x: 0, y: 0 },
      };
      return {
        type: "location",
        id,
        diff: { clip },
      };
    });
    group.update(updates);
  }

  function removeClips() {
    const ids = Array.isArray(loc.id) ? loc.id : [loc.id];
    const clipped = ids.filter((id) => $group.locations[id]?.clip);
    const updates = clipped.map((id) => ({
      type: "location",
      id,
      diff: { clip: null },
    }));
    group.update(updates);
  }

  function getTitle(uniqLocations, locations, location, grp) {
    if (uniqLocations.length > 1) return `${locations.length} locations selected`;
    const record = locRecord(location, grp);
    if (!record) return "";
    return `Location: ${record.mark}`;
  }

  function multiRecordType(ul) {
    if (ul.length <= 0) return null;
    if (ul.length === 1) return ul[0].record_type;

    let first = ul[0].record_type;
    for (let i = 1; i < ul.length; i++) {
      if (ul[i].record_type !== first) {
        return "Mixed";
      }
    }
    return first;
  }

  function makeMultiRecord(ul, grp, cc) {
    if (ul.length <= 1) return null;
    const recType = multiRecordType(ul);
    if (recType === "type") {
      const types = ul.map((loc) => grp.types[loc.record_id]);
      return makeMultiType(grp, types);
    } else if (recType === "item") {
      const items = ul.map((loc) => grp.items[loc.record_id]);
      return makeMultiItem(grp, items, cc);
    } else if (recType === "collection") {
      const colls = ul.map((loc) => grp.items[loc.record_id]);
      return makeMultiItem(grp, colls, cc);
    } else if (recType === "document_note") {
      const notes = ul.map((loc) => grp.document_notes[loc.record_id]);
      return makeMultiNote(notes);
    }

    return null;
  }

  function getClipScale() {
    if (loc.clip === "Mixed") {
      let val = locations[0].clip?.drawing_scale || null;
      for (let i = 1; i < locations.length; i++) {
        const l = locations[i];
        if (l.clip?.drawing_scale !== val) {
          val = "Mixed";
          break;
        }
      }
      return val || null;
    } else {
      return loc.clip?.drawing_scale || null;
    }
  }

  function clipScaleOptions(clipScale) {
    if (clipScale === "Mixed") {
      return [
        { label: "Mixed", value: "Mixed" },
        { label: "None", value: null },
        { label: "Set Custom Scale", value: "custom", unclickable: true },
        ...scaleOptions,
      ];
    } else if (clipScale && !definedMultipliers[clipScale]) {
      return [
        { label: "None", value: null },
        { label: "Set Custom Scale", value: "custom", unclickable: true },
        ...scaleOptions,
        { label: `Custom Scale: ${clipScale}`, value: clipScale },
      ];
    } else {
      return [
        { label: "None", value: null },
        { label: "Set Custom Scale", value: "custom", unclickable: true },
        ...scaleOptions,
      ];
    }
  }

  function refresh() {
    refreshKey = crypto.randomUUID();
  }

  function countLocations(locations) {
    return locations.reduce((counts, loc) => {
      if (counts[loc.record_id]) {
        counts[loc.record_id] += 1;
      } else {
        counts[loc.record_id] = 1;
      }
      return counts;
    }, {});
  }

  function record(loc, allAttachments) {
    if (!loc) return null;
    if (loc.record_type === "type") {
      return {
        ...$group.types[loc.record_id],
        attachments: allAttachments[loc.record_id] || [],
      };
    } else if (loc.record_type === "document_note") {
      return $group.document_notes[loc.record_id];
    } else {
      return {
        ...$group.items[loc.record_id],
        attachments: allAttachments[loc.record_id] || [],
      };
    }
  }

  async function updateLocation(prop, value) {
    if (Array.isArray(loc.id)) {
      const updates = loc.id.map((id) => ({
        type: "location",
        id,
        diff: { [prop]: value },
      }));
      group.update(updates);
    } else {
      group.updateLocation(loc.id, { [prop]: value });
    }
  }

  async function updateItem(evt) {
    const { id, prop, value, diff } = evt.detail;
    group.updateItem(id, prop, value, diff);
  }

  async function updateType(evt) {
    const { id, prop, value, diff } = evt.detail;
    group.updateType(id, prop, value, diff);
  }

  async function updateNote(evt) {
    const { id, prop, value, diff } = evt.detail;
    group.updateDocumentNote(id, prop, value, diff);
  }

  function updateMultiType(e) {
    const { prop, value } = e.detail;
    const ids = uniqLocations.map((l) => l.record_id);
    group.updateMultipleTypes(ids, prop, value);
  }

  function updateMultiItem(e) {
    const { prop, value, diff } = e.detail;
    const ids = uniqLocations.map((l) => l.record_id);
    group.updateMultipleItems(ids, prop, value, diff);
  }

  function updateMultiNote(e) {
    const { prop, value, diff } = e.detail;
    const ids = uniqLocations.map((l) => l.record_id);
    const updates = ids.map((id) => ({
      type: "document_note",
      id,
      prop,
      value,
      diff,
    }));
    group.update(updates);
  }

  function confirmChangeAssociation() {
    associationType = null;
    newAssociation = null;
    changeAssocModal.open();
  }

  function numParser(v) {
    const result = parseFloat(v);
    if (isNaN(result)) throw new Error("Not parseable as a number");
    if (result <= 0) throw new Error("Value must be positive");
    return result;
  }

  function changeAssociation() {
    if (newAssociation === "current-type") {
      const locs = Array.isArray(loc.id) ? loc.id : [loc.id];
      const updates = locs
        .map((id) => {
          const loc = $group.locations[id];
          if (!loc) return null;
          if (loc.record_type !== "item") {
            return null;
          }
          const item = locRecord(loc, $group);
          if (item?.type_id != null) {
            return {
              type: "location",
              id,
              diff: {
                record_type: "type",
                record_id: item.type_id,
              },
            };
          } else {
            return null;
          }
        })
        .filter((l) => l != null);

      if (updates.length) {
        group.update(updates);
      }

      if (locs.length > updates.length) {
        alerts.add({
          type: "warning",
          message: `${locs.length - updates.length} marker(s) could not be reassigned.`,
        });
      }

      return;
    } else if (newAssociation === "current-opening") {
      const locs = Array.isArray(loc.id) ? loc.id : [loc.id];
      const updates = locs
        .map((id) => {
          const loc = $group.locations[id];
          if (!loc) return null;
          if (loc.record_type !== "item") {
            return null;
          }
          const item = locRecord(loc, $group);
          if (item?.collection_id != null) {
            return {
              type: "location",
              id,
              diff: {
                record_type: "collection",
                record_id: item.collection_id,
              },
            };
          } else {
            return null;
          }
        })
        .filter((l) => l != null);

      if (updates.length) {
        group.update(updates);
      }

      if (locs.length > updates.length) {
        alerts.add({
          type: "warning",
          message: `${locs.length - updates.length} marker(s) could not be reassigned.`,
        });
      }

      return;
    }

    if (!associationType || !newAssociation) {
      return;
    }

    const diff = {
      record_type: associationType,
      record_id: newAssociation,
    };

    if (Array.isArray(loc.id)) {
      const updates = loc.id.map((id) => ({ type: "location", id, diff }));
      group.update(updates);
    } else {
      group.updateLocation(loc.id, diff);
    }
  }

  function makeUnique() {
    const locsByRecordId = locations.reduce(
      (acc, loc) => {
        if (!acc[loc.record_type][loc.record_id]) {
          acc[loc.record_type][loc.record_id] = [];
        }
        acc[loc.record_type][loc.record_id].push(loc);
        return acc;
      },
      { item: {}, collection: {}, type: {}, document_note: {} },
    );

    const updates = [];
    const locUpdates = [];

    Object.keys(locsByRecordId).forEach((recordType) => {
      const rt = locsByRecordId[recordType];
      Object.keys(rt).forEach((recordId) => {
        const locs = rt[recordId];
        if (locs.length <= 1) return;
        const updateType = recordType === "collection" ? "item" : recordType;

        const recUpdate = { type: updateType, action: "add", records: [], block: true };
        const firstRecord = locRecord(locs[0], $group);
        let mark = `${firstRecord.mark}.00`;

        // If these are all of the markers for this item in the drawing,
        // we will rename the first rather than make it distinct. Otherwise
        // we will make the first one unique as well.
        let locsToUniquify;
        if (locationsByRecord[recordId]?.length > locs.length) {
          locsToUniquify = locs;
        } else {
          locsToUniquify = locs.slice(1);
          mark = incrementMark(mark);
          updates.push({
            type: updateType,
            id: firstRecord.id,
            prop: "mark",
            value: mark,
          });
        }

        locsToUniquify.forEach((loc) => {
          const record = cloneDeep(locRecord(loc, $group));
          record.id = crypto.randomUUID();
          mark = incrementMark(mark);
          record.mark = mark;
          recUpdate.records.push(record);
          locUpdates.push({ type: "location", id: loc.id, prop: "record_id", value: record.id });
        });
        updates.push(recUpdate);
      });
    });

    updates.push(...locUpdates);
    group.update(updates);
  }

  async function setClipImage() {
    const records = {};
    locations.forEach((loc) => {
      if (loc.clip && !records[loc.record_id]) {
        records[loc.record_id] = loc.id;
      }
    });
    let recordsToUpdate = allLocsImaged
      ? Object.values(records)
      : Object.values(records).filter((locid) => !clipIsDrawing($group.locations[locid]));
    const updatedLocs = recordsToUpdate.map((locid) => locationDrawingUpdate(locid, $group));
    const updates = await Promise.all(updatedLocs);
    const u = updates.filter((upd) => upd != null);
    if (u.length) {
      group.update(u);
    }
  }

  async function updateClipScale(e) {
    const value = e.detail.value;
    if (value === "Mixed") {
      return;
    }
    const records = {};
    const updates = [];
    locations.forEach((loc) => {
      if (loc.clip) {
        // Rescales drawing_offset with the scale so the drawing resizes
        // about the frame origin rather than the crop-box corner; the height
        // lets an untouched frame snap back to its registration point.
        const record = locRecord(loc, $group);
        const clip = withClipScale(loc.clip, value, {
          heightInches: record?.cache?.height_in,
        });
        updates.push({
          type: "location",
          id: loc.id,
          diff: { clip },
        });
        if (record?.drawing?.attachment_id === loc.id && !records[record.id]) {
          records[record.id] = true;
          const drawing = cloneDeep(record.drawing);
          drawing.clip = clip;
          updates.push({
            type: loc.record_type === "collection" ? "item" : loc.record_type,
            id: record.id,
            diff: { drawing },
          });
        }
      }
    });
    if (updates.length) {
      group.update(updates);
    }
  }

  async function importLocationsFromAi() {
    requestingAiImport = true;
    const locs = uniqLocations.filter((loc) => loc.record_type === "collection" && loc.clip);

    spinners.select(
      ...locs.map((loc) => ({
        id: loc.id,
        index: visibleIndices[loc.page_id],
        x: loc.position.x,
        y: loc.position.y,
      })),
    );

    const notifier = await analyzeOpeningClips(
      $group,
      locs,
      $singleOpeningLlmModel,
      $singleOpeningReasoningLevel,
      $org.id,
      $fileData,
    );

    notifier.on("update", (updates) => {
      group.update(updates);
    });

    notifier.on("location-result", ({ id }) => {
      spinners.deselect(id);
    });

    notifier.on("error", (error) => {
      alerts.add({ type: "error", message: "Unable to import." });
      requestingAiImport = false;
      spinners.deselect(...locs.map((loc) => loc.id));
    });

    notifier.on("done", (result) => {
      if (result.successCount) {
        alerts.add({ type: "ok", message: `Successfully imported ${result.successCount} clips.` });
      }

      if (result.failCount) {
        alerts.add({ type: "error", message: `Failed to import ${result.failCount} clips.` });
      }
      requestingAiImport = false;
      spinners.deselect(...locs.map((loc) => loc.id));
    });
  }

  async function promptAiForLocation() {
    requestingAiImport = true;
    const locs = uniqLocations.filter((loc) => loc.record_type === "document_note" && loc.clip);
    spinners.select(
      ...locs.map((loc) => ({
        id: loc.id,
        index: visibleIndices[loc.page_id],
        x: loc.position.x,
        y: loc.position.y,
      })),
    );
    const images = await Promise.all(locs.map((loc) => locationImage(loc, $group)));
    const response = await promptLocationsParallel({
      locations: locs.map((loc, i) => {
        return {
          id: loc.id,
          record_id: loc.record_id,
          clip: loc.clip,
          image: images[i],
        };
      }),
      model: $noteLlmModel,
      reasoning_level: $noteReasoningLevel,
      prompt: $notePrompt,
      organization_id: $org.id,
    });

    const stream = new PostStream(response);

    let goodResults = 0;
    let badResults = 0;
    stream.on("location_result", (result) => {
      if (result.error || !result.response) {
        badResults += 1;
        group.update([
          {
            type: "document_note",
            id: result.record_id,
            diff: {
              approval_status: "flagged",
            },
          },
        ]);
      } else {
        goodResults += 1;
        const note = $group.document_notes[result.record_id];
        if (!note) return;

        group.update([
          {
            type: "document_note",
            id: result.record_id,
            diff: {
              body: result.response,
              prompt_text: $notePrompt,
              llm_request_log_id: result.llm_request_id,
            },
          },
        ]);
      }

      spinners.deselect(result.id);
    });

    stream.on("error", (error) => {
      alerts.add({ type: "error", message: "Failed prompt." });
      console.log(error);
      requestingAiImport = false;
      spinners.deselect(...locs.map((loc) => loc.id));
    });

    stream.on("done", () => {
      if (goodResults) {
        alerts.add({ type: "ok", message: `Successfully updated ${goodResults} notes.` });
      }

      if (badResults) {
        alerts.add({ type: "error", message: `Failed to update ${badResults} notes.` });
      }

      requestingAiImport = false;
      spinners.deselect(...locs.map((loc) => loc.id));
    });
  }

  function locationAction(value) {
    if (value === "change-association") {
      confirmChangeAssociation();
    } else if (value === "create-clips") {
      createClips();
    } else if (value === "remove-clips") {
      removeClips();
    } else if (value === "make-unique") {
      makeUnique();
    } else if (value === "set-clip-image") {
      setClipImage();
    } else if (value === "ai-import") {
      importLocationsFromAi();
    } else if (value === "ai-prompt") {
      confirmPromptModal.open();
    }
  }

  function assignToOpening() {
    const opening = locations.filter((loc) => loc.record_type === "collection")[0];
    if (!opening) {
      alerts.add({ type: "error", message: "No opening found to assign to." });
      return;
    }

    const updates = locations
      .filter((loc) => loc.record_type === "item" && loc.page_id === opening.page_id)
      .map((loc) => ({
        type: "item",
        id: loc.record_id,
        diff: { collection_id: opening.record_id },
      }));

    if (!updates.length) {
      alerts.add({ type: "error", message: "No item markers found on the same page as the opening." });
      return;
    }

    group.update(updates);
  }
</script>

{#if uniqLocations.length > 1}
  <SidebarTitle>
    {title}
  </SidebarTitle>
{/if}
<SelectedLocations
  single={uniqLocations.length === 1}
  {group}
  {locations}
  {itemsWithLocs}
  {collectionsWithLocs}
  {documentNotesWithLocs}
  {locationCounts}
  {typesWithLocs}
  selectedDimensionsList={dimensions}
  on:add-location
  on:clone-location />
{#if locations.length && dimensions.length}
  <div class="px-6 text-xs text-gray-500 font-bold italic">Mixed selection</div>
{:else if dimensions.length}
  <DimensionProperties
    {dimensions}
    {disabled}
    {group}
    {items}
    {collections}
    {types}
    {doc}
    locations={docLocations} />
{:else if locations.length}
  <SidebarSection>
    <div class="flex items-center justify-between">
      <h3 class="px-2 mb-2">Marker</h3>
      <button
        class="p-1 rounded hover:bg-gray-200"
        use:tooltip={{ text: "Show Advanced Settings" }}
        on:click={() => (showAdvancedLocationSettings = !showAdvancedLocationSettings)}>
        <EllipsisIcon />
      </button>
    </div>
    {#if loc?.show_checkbox === true}
      <BooleanInput
        {disabled}
        label="Checked"
        labelWidth="7.5rem"
        value={loc?.checked}
        on:input={(e) => updateLocation("checked", e.detail.value)} />
    {/if}
    <TextInput
      {disabled}
      label="Category"
      labelWidth="7.5rem"
      value={loc?.category}
      on:input={(e) => updateLocation("category", e.detail.value)} />
    <BooleanInput
      {disabled}
      label="Ignore in Counts"
      labelWidth="7.5rem"
      value={loc?.ignore_for_auto_count}
      on:input={(e) => updateLocation("ignore_for_auto_count", e.detail.value)} />
    {#key locations}
      <ButtonInput
        {disabled}
        label="Actions"
        labelWidth="7.5rem"
        value={true}
        buttons={[
          { component: LocationItemIcon, label: "Change Association", value: "change-association" },
          ...(locations.length > 1
            ? [{ component: LocationItemCloneMultiple, label: "Make Unique", value: "make-unique" }]
            : []),
          ...(allLocsClipped
            ? [{ component: ClipIcon, label: "Remove Clip", value: "remove-clips", active: true }]
            : [{ component: ClipIcon, label: "Create Clip", value: "create-clips" }]),
          {
            component: ImageIcon,
            label: "Toggle Clip Image",
            value: "set-clip-image",
            active: allLocsImaged,
          },
          ...(hasCollection
            ? [
                {
                  component: requestingAiImport ? SpinnerIcon : AiIcon,
                  label: "AI Import",
                  value: "ai-import",
                  disabled: requestingAiImport,
                  spin: requestingAiImport,
                },
              ]
            : []),
          ...(allLocsNotes
            ? [
                {
                  component: requestingAiImport ? SpinnerIcon : AiIcon,
                  label: "AI Prompt",
                  value: "ai-prompt",
                  disabled: requestingAiImport,
                  spin: requestingAiImport,
                },
              ]
            : []),
        ]}
        on:input={(e) => locationAction(e.detail.value)} />
    {/key}
    {#key loc}
      {#if loc?.clip}
        {@const clipScale = getClipScale()}
        {@const options = clipScaleOptions(clipScale)}
        <SelectInput
          {disabled}
          label="Clip Scale"
          labelWidth="7.5rem"
          value={clipScale}
          {options}
          on:option-click={() => customScaleModal.open(clipScale)}
          on:input={updateClipScale} />
      {/if}
    {/key}
    {#if locTypeCounts.collection === 1 && locTypeCounts.item >= 1}
      <ButtonInput
        {disabled}
        label="Assign to Opening"
        labelWidth="7.5rem"
        value={null}
        buttons={[{ component: LocationCollIcon, label: "Assign", value: "current-opening" }]}
        on:input={assignToOpening} />
    {/if}
    {#if showAdvancedLocationSettings}
      <SelectInput
        {disabled}
        label="Representation"
        labelWidth="7.5rem"
        value={loc?.representation}
        options={[
          { label: "Mark in Shape", value: "mark_in_shape" },
          { label: "Symbol Only", value: "symbol_only" },
          { label: "Content", value: "content" },
          { label: "Content with Symbol", value: "content_with_symbol" },
        ]}
        on:input={(e) => updateLocation("representation", e.detail.value)} />
      <SelectInput
        {disabled}
        label="Shape"
        labelWidth="7.5rem"
        value={loc?.shape}
        options={[
          { label: "Default", value: "default" },
          { label: "Circle", value: "circle" },
          { label: "Square", value: "rectangle" },
          { label: "Hexagon", value: "hexagon" },
        ]}
        on:input={(e) => updateLocation("shape", e.detail.value)} />
      <SelectInput
        {disabled}
        label="Symbol"
        labelWidth="7.5rem"
        value={loc?.symbol}
        options={[
          { icon: BlankIcon, label: "Default", value: "default" },
          { icon: BlankIcon, label: "None", value: "none" },
          { icon: CircleIcon, label: "Circle", value: "circle" },
          { icon: SquareIcon, label: "Square", value: "square" },
          { icon: HexagonIcon, label: "Hexagon", value: "hexagon" },
          { icon: NoteIcon, label: "Note", value: "note" },
          { icon: TriangleIcon, label: "Triangle", value: "triangle" },
          { icon: DiamondIcon, label: "Diamond", value: "diamond" },
          { icon: AnchorIcon, label: "Anchor", value: "anchor" },
          { icon: HardSpliceIcon, label: "Hard Splice", value: "hard_splice" },
          { icon: WorkingSpliceIcon, label: "Working Splice", value: "working_splice" },
        ]}
        on:input={(e) => updateLocation("symbol", e.detail.value)} />
      <SelectInput
        {disabled}
        label="Scale Reference"
        labelWidth="7.5rem"
        value={loc?.scale_reference}
        options={[
          { label: "Screen Space", value: "screen_space" },
          { label: "Paper Space", value: "paper_space" },
        ]}
        on:input={(e) => updateLocation("scale_reference", e.detail.value)} />
      <TextInput
        {disabled}
        label="Marker Text Size"
        labelWidth="7.5rem"
        value={loc?.size}
        postlabel={loc?.scale_reference === "screen_space" ? "px" : "pts"}
        parser={numParser}
        on:input={(e) => updateLocation("size", e.detail.value)} />
      <ColorInput
        {disabled}
        label="Color Override"
        labelWidth="7.5rem"
        value={loc?.color_override}
        nullable
        on:input={(e) => updateLocation("color_override", e.detail.value)} />
      <BooleanInput
        {disabled}
        label="Show Checkbox"
        labelWidth="7.5rem"
        value={loc?.show_checkbox}
        on:input={(e) => updateLocation("show_checkbox", e.detail.value)} />
    {/if}
  </SidebarSection>
  {#if multiRecord && location.record_type === "type"}
    <SidebarTitle title="Type Properties" />
    <TypeProperties
      {disabled}
      type={multiRecord}
      settings={typeSettings}
      multiple
      on:updateType={updateMultiType}
      on:updateSupplier />
  {:else if multiRecord && location.record_type === "document_note"}
    <SidebarTitle title="Note Properties" />
    <DocumentNoteProperties
      {group}
      noteid={multiRecord.id}
      {disabled}
      note={multiRecord}
      {documentNotes}
      multiple
      on:updateNote={updateMultiNote} />
  {:else if multiRecord}
    <ItemProperties
      {group}
      {disabled}
      item={multiRecord}
      customColumns={customCols}
      {types}
      {collections}
      {standardColumns}
      isCollection={location.record_type === "collection"}
      settings={$group.data.settings}
      multiple
      on:updateItem={updateMultiItem} />
  {:else if uniqLocations.length === 1 && location.record_type === "type"}
    {#key rec.id}
      <TypeProperties
        {disabled}
        type={rec}
        settings={typeSettings}
        on:updateType={updateType}
        on:updateSupplier />
      {#if rec?.data?.layers}
        <LayerProperties type={rec} {group} {disabled} on:updateType={updateType} />
      {/if}
      {#key $expandRightPanel}
        <LocationThumbnailViewer recordid={rec.id} {group} record={rec} {disabled} />
      {/key}
      <Attachments attachments={rec.attachments || []} {group} typeid={rec.id} {disabled} />
      <History
        updatedBy={rec.updater}
        updatedOn={rec.updated_at}
        approvedBy={rec.approver}
        approvalStatus={rec.approval_status}
        approvedOn={rec.approval_status_updated_at} />
    {/key}
  {:else if uniqLocations.length === 1 && location.record_type === "document_note"}
    {#key rec.id}
      <DocumentNoteProperties
        {group}
        noteid={rec.id}
        {disabled}
        note={rec}
        {documentNotes}
        on:updateNote={updateNote} />
      {#key $expandRightPanel}
        <LocationThumbnailViewer recordid={rec.id} {group} record={rec} {disabled} />
      {/key}
    {/key}
  {:else if uniqLocations.length === 1}
    {#key rec.id}
      <ItemProperties
        {group}
        itemid={rec.id}
        {disabled}
        item={rec}
        {collections}
        customColumns={customCols}
        {types}
        {standardColumns}
        {settings}
        isCollection={rec.is_collection}
        showEditor={$expandRightPanel}
        on:updateItem={updateItem} />
      {#if rec.is_collection}
        <CollectionItemList
          {group}
          {items}
          collectionId={rec.id}
          collectionItems={collectionItems[rec.id]}
          {disabled} />
      {/if}
      {#key $expandRightPanel}
        <LocationThumbnailViewer recordid={rec.id} {group} record={rec} {disabled} />
      {/key}
      <Attachments attachments={rec.attachments || []} {group} itemid={rec.id} {disabled} />
      <History
        updatedBy={rec.updater}
        updatedOn={rec.updated_at}
        approvedBy={rec.approver}
        approvalStatus={rec.approval_status}
        approvedOn={rec.approval_status_updated_at} />
    {/key}
  {:else}
    <div class="px-6 text-xs text-gray-500 font-bold italic">Mixed selection</div>
  {/if}
{/if}

<Modal
  closeable
  bind:this={changeAssocModal}
  on:confirm={changeAssociation}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Change Association", type: "confirm", disabled: !newAssociation, style: "primary" },
  ]}>
  <div slot="title">Change Association</div>
  <div slot="content" class="space-y-4 text-xs">
    <p>Choose a new record to associate with this mark:</p>
    <SelectInput
      label="Record Type"
      labelWidth="6rem"
      options={[
        { label: "Item", value: "item" },
        { label: "Opening", value: "collection" },
        { label: "Type", value: "type" },
        { label: "Document Note", value: "document_note" },
      ]}
      bind:value={associationType} />
    {#if associationType}
      <SelectInput
        labelWidth="6rem"
        label={recordTypes[associationType]}
        options={associationOptions}
        bind:value={newAssociation} />
    {/if}
    <p>Or reassign to:</p>
    <div class="flex gap-4 items-center px-2">
      <div class="flex gap-1 items-center">
        <label for="current-type" class="text-gray-500">Current Type</label>
        <input type="radio" name="current-type" value="current-type" bind:group={newAssociation} />
      </div>

      <div class="flex gap-1 items-center">
        <label for="current-opening" class="text-gray-500">Current Opening</label>
        <input type="radio" name="current-opening" value="current-opening" bind:group={newAssociation} />
      </div>
    </div>
  </div>
</Modal>

<Modal
  closeable
  bind:this={confirmPromptModal}
  on:confirm={promptAiForLocation}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Submit", type: "confirm", disabled: !$notePrompt, style: "primary" },
  ]}>
  <div slot="title">AI Prompt</div>
  <div slot="content" class="space-y-2 text-xs">
    <div>
      <p>Enter a prompt for this clip:</p>
      <TextareaInput fullwidth placeholder="Ask the AI for help." bind:value={$notePrompt} rows={5} />
    </div>
    {#if $profile?.user_role === "developer"}
      <SelectInput
        border
        label="Model"
        bind:value={$noteLlmModel}
        options={[
          { label: "OpenAI GPT-5.4", value: "openai-gpt-5.4" },
          { label: "OpenAI GPT-5.4 mini", value: "openai-gpt-5.4-mini" },
          { label: "OpenAI GPT-5.2", value: "openai-gpt-5.2" },
          { label: "OpenAI GPT-4", value: "openai-gpt-4" },
          { value: "google-gemini-pro", label: "Google Gemini Pro (3.1)" },
          { label: "Claude Sonnet 4.6", value: "claude-sonnet" },
          { value: "claude-opus", label: "Claude Opus 4.6" },
        ]} />
      <SelectInput
        border
        label="Reasoning Effort"
        bind:value={$noteReasoningLevel}
        options={[
          { label: "Low", value: "low" },
          { label: "Medium", value: "medium" },
          { label: "High", value: "high" },
        ]} />
    {/if}
  </div>
</Modal>
<CustomScaleModal bind:this={customScaleModal} on:setScale={updateClipScale} />
